import React from "react";

/**
 * Generic green crescent icon for halal status display.
 * NOT a trademarked logo — a universally recognizable crescent symbol.
 *
 * Props:
 *   filled: boolean — true = "Halal Certified" (solid fill), false = "Halal Friendly" (outline only)
 *   size: number — icon size in pixels (default 16)
 *   className: string — additional CSS classes
 */
export const HalalCrescent = ({ filled = true, size = 16, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    data-testid={filled ? "halal-crescent-filled" : "halal-crescent-outline"}
  >
    {filled ? (
      /* Filled crescent — Halal Certified */
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c1.82 0 3.53-.49 5-1.35A8 8 0 0 1 10 12a8 8 0 0 1 7-7.92A9.96 9.96 0 0 0 12 2z"
        fill="#16a34a"
      />
    ) : (
      /* Outline crescent — Halal Friendly */
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c1.82 0 3.53-.49 5-1.35A8 8 0 0 1 10 12a8 8 0 0 1 7-7.92A9.96 9.96 0 0 0 12 2z"
        fill="none"
        stroke="#16a34a"
        strokeWidth="1.5"
      />
    )}
  </svg>
);

/**
 * Dietary status pills for business cards and offer detail pages.
 *
 * Renders pills for: Halal Certified, Halal Friendly, Vegan, Vegetarian, Boycott (hidden).
 *
 * Props:
 *   dietary: object — the dietary sub-document from the business
 *   boycottFlag: boolean — whether this business is boycotted
 *   showBoycott: boolean — whether the current user has boycott pill unlocked
 *   compact: boolean — smaller pills for card view (default true)
 */
export const DietaryPills = ({
  dietary,
  boycottFlag = false,
  showBoycott = false,
  compact = true,
}) => {
  if (!dietary && !boycottFlag) return null;

  const d = dietary || {};
  const pills = [];
  const sz = compact ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-1";

  // Halal Certified
  if (d.halal_status === "certified") {
    pills.push(
      <span
        key="halal-cert"
        className={`${sz} inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 rounded font-semibold`}
        data-testid="pill-halal-certified"
      >
        <HalalCrescent filled size={compact ? 12 : 14} />
        Halal Certified{d.halal_certifier ? ` (${d.halal_certifier})` : ""}
      </span>
    );
  }

  // Halal Friendly
  if (d.halal_status === "friendly") {
    pills.push(
      <span
        key="halal-friendly"
        className={`${sz} inline-flex items-center gap-1 bg-green-50/60 text-green-600 border border-green-200/60 rounded font-semibold`}
        data-testid="pill-halal-friendly"
      >
        <HalalCrescent filled={false} size={compact ? 12 : 14} />
        Halal Friendly
      </span>
    );
  }

  // Vegan
  if (d.vegan_status === "fully_vegan") {
    pills.push(
      <span
        key="vegan"
        className={`${sz} bg-lime-50 text-lime-700 border border-lime-200 rounded font-semibold`}
        data-testid="pill-vegan"
      >
        Vegan
      </span>
    );
  } else if (d.vegan_status === "friendly") {
    pills.push(
      <span
        key="vegan-opt"
        className={`${sz} bg-lime-50/70 text-lime-600 border border-lime-200/70 rounded font-semibold`}
        data-testid="pill-vegan-options"
      >
        Vegan Options
      </span>
    );
  }

  // Vegetarian
  if (d.vegetarian_status === "fully_vegetarian") {
    pills.push(
      <span
        key="veg"
        className={`${sz} bg-emerald-50 text-emerald-700 border border-emerald-200 rounded font-semibold`}
        data-testid="pill-vegetarian"
      >
        Vegetarian
      </span>
    );
  } else if (d.vegetarian_status === "friendly") {
    pills.push(
      <span
        key="veg-opt"
        className={`${sz} bg-emerald-50/70 text-emerald-600 border border-emerald-200/70 rounded font-semibold`}
        data-testid="pill-vegetarian-options"
      >
        Vegetarian Options
      </span>
    );
  }

  // Boycott — hidden by default, only shown when unlocked
  if (boycottFlag && showBoycott) {
    pills.push(
      <span
        key="boycott"
        className={`${sz} bg-red-50 text-red-600 border border-red-200 rounded font-semibold`}
        data-testid="pill-boycott"
      >
        Boycott
      </span>
    );
  }

  // Gluten Free
  if (d.gluten_free_certified) {
    pills.push(
      <span
        key="gf-cert"
        className={`${sz} bg-amber-50 text-amber-700 border border-amber-200 rounded font-semibold`}
        data-testid="pill-gluten-free-certified"
      >
        Gluten Free Certified
      </span>
    );
  } else if (d.gluten_free_friendly) {
    pills.push(
      <span
        key="gf-friendly"
        className={`${sz} bg-amber-50/70 text-amber-600 border border-amber-200/70 rounded font-semibold`}
        data-testid="pill-gluten-free-friendly"
      >
        Gluten Free Options
      </span>
    );
  }

  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1" data-testid="dietary-pills">
      {pills}
    </div>
  );
};
